//july 18 FormThemeContext.js:
//import pako from 'pako';
import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import themes from '../components/FormSettings/FormThemeSettings/FormThemeConfig';
import { useFormContext } from './FormContext'; // Import FormContext to use formId
import { getDoc, doc } from 'firebase/firestore'; // Firebase imports
import { db } from '../firebase'; // Firebase instance
import { decompressData } from '../utils/decompressionUtils'; // Decompression utility
import { useLoading } from './LoadingContext'; // Import LoadingContext
import { safeGetItem, safeSetItem } from '../utils/storageUtils';

const FormThemeContext = createContext();

export const useFormTheme = () => useContext(FormThemeContext);

export const FormThemeProvider = ({ children }) => {
  const { formId: currentFormId } = useFormContext(); // Retrieve formId from FormContext
  const { setLoading } = useLoading(); // Use setLoading from LoadingContext
  const [themeLoading, setThemeLoading] = useState(true); // Initially true since we haven't fetched the theme yet

  //  Add formIdReady state
  const [formIdReady, setFormIdReady] = useState(false);

const isPublicPreviewMode = useMemo(() => {
  try {
    const raw = sessionStorage.getItem("isPublicPreview");
    const parsed = JSON.parse(raw);
    return parsed?.value === true;
  } catch (e) {
    return false;
  }
}, []);

/**
 * True when the author is inside the form‑builder UI.
 * Viewer / published pages live under “/forms/…”, so this
 * lets us decide whether cached drafts should win or the
 * published Firestore copy should overwrite them.
 */
const isBuilderRoute = useMemo(() => {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.startsWith('/form-builder');
}, []);

  //  Track formId readiness
  useEffect(() => {
    if (currentFormId) {
      setFormIdReady(true);
       console.log(`[FormThemeContext] formIdReady set to true for formId: ${currentFormId}`);
    } else {
      setFormIdReady(false);
       console.log(`[FormThemeContext] formIdReady set to false (no formId yet)`);
    }
  }, [currentFormId]);

  // ────────────────────────────────────────────────────────────────
  // Mode-aware theme cache keys (aligned with FormLayoutContext)
  // - Builder route: honor builder view mode (auto/published/draftOverlay)
  // - Viewer route: ALWAYS force published
  // ────────────────────────────────────────────────────────────────
  const builderViewMode = useMemo(() => {
    try {
      if (!currentFormId) return 'auto';
      const raw = sessionStorage.getItem(`form_${currentFormId}_layoutVersionMode`);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed?.value || 'auto';
    } catch (e) {
      return 'auto';
    }
  }, [currentFormId]);

  const effectiveViewMode = useMemo(() => {
    if (isBuilderRoute) return builderViewMode;
    return 'published';
  }, [isBuilderRoute, builderViewMode]);

  // Legacy keys (pre-mode-aware). Keep for migration / fallback.
  const makeLegacyKey = useCallback(
    (key) => `form_${currentFormId}_${key}`,
    [currentFormId]
  );

  // Mode-aware keys (prevents published cache masking draft overlay and vice versa)
  const makeKey = useCallback(
    (key) => `form_${currentFormId}_${key}__${effectiveViewMode}`,
    [currentFormId, effectiveViewMode]
  );

  // TIMESTAMP LOGIC: keep all theme-related updates under one key (mode-aware)
  const makeLastUpdatedKey = useCallback(
    () => `form_${currentFormId}_themeLastUpdated__${effectiveViewMode}`,
    [currentFormId, effectiveViewMode]
  );

  // Legacy timestamp key (fallback)
  const makeLegacyLastUpdatedKey = useCallback(
    () => `form_${currentFormId}_themeLastUpdated`,
    [currentFormId]
  );

  // Function to get data using storageUtils.js
  const getFromStorage = useCallback((key, legacyKey = null) => {
    // • Builder (or headless preview) → may use cached drafts
    // • Viewer / published page        → ignore cache, force Firestore
    if ((!isBuilderRoute && !isPublicPreviewMode) || !formIdReady) return null;

    const storedData = safeGetItem(key) || (legacyKey ? safeGetItem(legacyKey) : null);
    if (storedData) {
//      console.log(`[FormThemeContext: getFromStorage] Retrieved ${key} (or legacy) for formId: ${currentFormId}`, storedData);
      return storedData;
    }

    console.warn(
      `[FormThemeContext: getFromStorage] No data found for ${key}${legacyKey ? ` (legacy: ${legacyKey})` : ''} for formId: ${currentFormId}`
    );
    return null;
  }, [currentFormId, formIdReady, isBuilderRoute, isPublicPreviewMode]);

  // Function to save data to sessionStorage and backup to localStorage
  const saveToStorage = useCallback((key, value, legacyKey = null) => {
    if ((!isBuilderRoute && !isPublicPreviewMode) || !formIdReady) return;

    safeSetItem(key, value);

    // Optional migration mirror: keep legacy keys in sync so older code paths
    // (or helper utilities) still see the latest theme pieces.
    if (legacyKey) {
      safeSetItem(legacyKey, value);
    }
}, [currentFormId, formIdReady, isBuilderRoute, isPublicPreviewMode]);

  // Default settings (unchanged)
  const defaultColors = useMemo(() => ({
    backgroundColor: '#f5f5f5',
    activeColor: '#1976d2',
    textColor: 'rgba(0, 0, 0, 0.6)', // ✅ MUI default gray for labels
    warningColor: '#ff0000',
  }), []);

  const defaultFonts = useMemo(() => ({
    primaryFont: 'Arial, sans-serif',
    secondaryFont: 'Roboto, sans-serif',
  }), []);

  const defaultSubmitButtonStyle = useMemo(() => ({
    alignment: 'center',
    label: 'Submit',
    backgroundColor: '#4285f4',
    textColor: '#FFFFFF',
    fontSize: '16px',
    fontWeight: '700',
    height: '50px',
    width: '150px',
    borderRadius: '5px',
    fontFamily: 'Roboto, sans-serif',
    borderColor: 'transparent',
    borderWidth: '1px',
    borderStyle: 'solid',
    boxShadow: 'none',
    textTransform: 'none',
  }), []);

  const previousFormId = useRef(currentFormId);
  /**
   * ❗ We must NOT write the default palette / fonts / etc.
   *    until we have either…
   *    1.  fetched Firestore-theme     **or**
   *    2.  loaded a cached palette from storage
   *
   *    Otherwise an anonymous visitor races ahead, writes the
   *    defaults to storage (and updates `lastUpdated`), after
   *    which the Firestore fetch sees “local is newer → skip”.
   */
  const isInitialThemeLoaded = useRef(false);

  /**
   * --------------------------------------------------------------
   *  Flip the guard the very first time the user edits any theme
   *  value.  This lets brand‑new forms start saving immediately
   *  without blocking saved / published ones.
   * --------------------------------------------------------------
   */
  const touchTheme = () => {
    if (!isInitialThemeLoaded.current) {
      isInitialThemeLoaded.current = true;
    }
  };

  // ────────────────────────────────────────────────────────────────
  // NEW: Treat selectedTheme changes as a first-class user edit
  // - Fixes "Full Page" not sticking because isInitialThemeLoaded guard
  //   can prevent the debounced save effect from persisting quickly enough.
  // - We persist immediately (mode-aware + legacy mirror), just like other
  //   handlers do for colors/fonts/variant/button.
  // ────────────────────────────────────────────────────────────────
  const setSelectedThemeSafe = useCallback((nextThemeName) => {
    if ((!isBuilderRoute && !isPublicPreviewMode) || !formIdReady) return;

    // Mark that a real theme edit happened so save effects are allowed
    touchTheme();

    // Update state first (UI responds immediately)
    setSelectedTheme(nextThemeName);

    // Persist immediately to avoid "route change beats debounce"
    // Mode-aware key + legacy mirror
    saveToStorage(makeKey('selectedTheme'), nextThemeName, makeLegacyKey('selectedTheme'));

    // Update lastUpdated (mode-aware + legacy mirror)
    saveToStorage(makeLastUpdatedKey(), new Date().toISOString(), makeLegacyLastUpdatedKey());

    console.log(`[FormThemeContext] setSelectedThemeSafe → persisted selectedTheme="${nextThemeName}"`, {
      formId: currentFormId,
      effectiveViewMode,
      isBuilderRoute,
      isPublicPreviewMode,
    });
  }, [
    formIdReady,
    isBuilderRoute,
    isPublicPreviewMode,
    saveToStorage,
    makeKey,
    makeLegacyKey,
    makeLastUpdatedKey,
    makeLegacyLastUpdatedKey,
    currentFormId,
    effectiveViewMode,
  ]);

  // State for theme and its settings

const [selectedTheme,     setSelectedTheme]     = useState('Paper');
const [colors,            setColors]            = useState(defaultColors);

const [fonts,             setFonts]             = useState(defaultFonts);
const [submitButtonStyle, setSubmitButtonStyle] = useState(defaultSubmitButtonStyle);
const [fieldVariant,      setFieldVariant]      = useState('outlined');

const [muiTheme, setMuiTheme] = useState(themes[selectedTheme || 'Paper']);

  // ---------------------------------------------------------------------------
  // UPDATE MUI THEME WHEN SELECTEDTHEME OR COLORS/FONTS CHANGE
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!formIdReady && !isPublicPreviewMode) return;
    if (!colors || !fonts || !selectedTheme)   return;   // wait for data

    // Grab the base theme (e.g. "Paper" or "Full Page")
    const baseTheme = themes[selectedTheme];

    // Inject your stored colors into MUI's palette & typography
    const updatedTheme = {
      ...baseTheme,
      palette: {
        ...baseTheme.palette,

        // Use `activeColor` for the "primary" palette
        primary: {
          ...baseTheme.palette.primary,
          main: colors.activeColor,
        },

        // Use `warningColor` for errors/warnings
        error: {
          ...baseTheme.palette.error,
          main: colors.warningColor,
        },

        // Background behind Paper or for "Full Page" layout
        background: {
          ...baseTheme.palette.background,
          default: colors.backgroundColor,
          paper:   colors.backgroundColor,
        },

        // Default text color
        text: {
          ...baseTheme.palette.text,
          primary: colors.textColor,
        },
      },

      // Set the user’s chosen primary font
      typography: {
        ...baseTheme.typography,
        fontFamily: fonts.primaryFont,
      },

      // Optionally, you can add component overrides if needed:
      // components: {
      //   ...baseTheme.components,
      //   MuiOutlinedInput: {
      //     styleOverrides: {
      //       root: {
      //         '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      //           borderColor: colors.activeColor,
      //         },
      //       },
      //     },
      //   },
      //   ...
      // },
    };

//    console.log(
//      `[FormThemeContext] Applied updated muiTheme with selected theme: ${selectedTheme}`,
//      updatedTheme
//    );

    // Dynamically update the context's muiTheme
    setMuiTheme(updatedTheme);

  }, [selectedTheme, colors, fonts, formIdReady, isPublicPreviewMode]);

  // ---------------------------------------------------------------------------
  // FETCH AND DECOMPRESS THEME DATA FROM FIRESTORE IF THE FORM EXISTS
  // BUT ONLY IF LOCAL DATA IS NOT NEWER
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!formIdReady && !isPublicPreviewMode) return;


    const loadThemeFromFirestore = async () => {
      if (!currentFormId) return;

      setLoading(true, 'themeSettings'); // Notify LoadingContext
      setThemeLoading(true); // Set internal theme loading state to true

      try {
        console.log(`[FormThemeContext] Fetching theme data for formId: ${currentFormId}`);

        const formRef = doc(db, 'forms', currentFormId);
        const formDoc = await getDoc(formRef);

        if (!formDoc.exists()) {
          console.warn(`[FormThemeContext] No theme data found in Firestore for formId: ${currentFormId}`);
          return;
        }

        const formData = formDoc.data() || {};

        // Builder/public-preview: honor effectiveViewMode (auto/published/draftOverlay)
        // Viewer (/forms/:id): force published/root
        const viewMode = (isBuilderRoute || isPublicPreviewMode) ? effectiveViewMode : 'published';

        // Resolve theme blob based on the chosen view mode.
        // We support a few possible overlay shapes defensively:
        // - formData.draftOverlay.theme
        // - formData.draftOverlay.compressedThemeData
        // - fallback to root formData.theme
        let compressedThemeData = null;

        if (viewMode !== 'published') {
          const overlay = formData?.draftOverlay || null;
          compressedThemeData =
            overlay?.theme ||
            overlay?.compressedThemeData ||
            null;
        }

        // Always fallback to root/published
        if (!compressedThemeData) {
          compressedThemeData = formData?.theme || null;
        }

        if (!compressedThemeData) {
          console.warn(
            `[FormThemeContext] No compressed theme data found for formId: ${currentFormId} (viewMode=${viewMode})`
          );
          return;
        }


//        console.log(`[FormThemeContext] Compressed theme data found. Beginning decompression.`);
        
        //  Use decompression utility for consistency
        const themeData = await decompressData(compressedThemeData);

//        console.log(`[FormThemeContext] Successfully decompressed theme data for formId: ${currentFormId}`, themeData);

        // TIMESTAMP LOGIC: Compare local lastUpdated with Firestore's
        const localLastUpdatedKey = makeLastUpdatedKey();
        const legacyLastUpdatedKey = makeLegacyLastUpdatedKey();
        const localLastUpdated = getFromStorage(localLastUpdatedKey, legacyLastUpdatedKey);
        const firestoreLastUpdated = themeData?.lastUpdated; // also e.g. "2025-02-27T..."

        // ------------------------------------------------------------
        // Builder keeps the draft‑wins behaviour.
        // Viewer / published page forces Firestore to win every time.
        // ------------------------------------------------------------
        if (!isBuilderRoute && !isPublicPreviewMode) {
          console.info('[FormThemeContext] Viewer route – forcing Firestore theme to replace any cached draft.');
        } else if (
          localLastUpdated &&
          firestoreLastUpdated &&
          localLastUpdated > firestoreLastUpdated
        ) {
          console.warn('[FormThemeContext] Local theme data is newer – skipping Firestore overwrite.');
          return;               // ← same early‑exit **only** in Builder
        }


        // If local has no timestamp, or Firestore is actually newer, we proceed
        // (Or if Firestore doesn't have a timestamp either, we still proceed so we can store it)
        if (themeData) {
          setSelectedTheme(themeData.selectedTheme || 'Paper');
         setColors(
           themeData.colors && typeof themeData.colors === 'object'
             ? { ...defaultColors, ...themeData.colors }   // ← merge, don’t replace
             : defaultColors
         );

          setFonts(themeData.fonts && typeof themeData.fonts === 'object' ? { ...defaultFonts, ...themeData.fonts } : defaultFonts);
          setSubmitButtonStyle(themeData.submitButtonStyle && typeof themeData.submitButtonStyle === 'object' ? { ...defaultSubmitButtonStyle,...themeData.submitButtonStyle } : defaultSubmitButtonStyle);
          setFieldVariant(themeData.fieldVariant || 'outlined');

        //tell the rest of the file that a real theme is now loaded
        isInitialThemeLoaded.current = true;   // ← NEW

          console.log(`[FormThemeContext] Theme settings applied successfully.`);

          // ✅ Immediately cache decompressed theme data using safeSetItem
          console.log(`[FormThemeContext] Caching decompressed theme data for formId: ${currentFormId}`);

          // TIMESTAMP LOGIC: If Firestore data had a lastUpdated, store it. Otherwise store new time
          const finalFirestoreTimestamp = firestoreLastUpdated || new Date().toISOString();

          // Save each piece
          saveToStorage(makeKey('selectedTheme'), themeData.selectedTheme || 'Paper', makeLegacyKey('selectedTheme'));
          saveToStorage(makeKey('colors'),
            themeData.colors && typeof themeData.colors === 'object'
              ? { ...defaultColors, ...themeData.colors }
              : defaultColors,
            makeLegacyKey('colors')
          );

          saveToStorage(makeKey('fonts'),
            themeData.fonts && typeof themeData.fonts === 'object'
              ? { ...defaultFonts, ...themeData.fonts }
              : defaultFonts,
            makeLegacyKey('fonts')
          );

          saveToStorage(makeKey('submitButtonStyle'),
            themeData.submitButtonStyle && typeof themeData.submitButtonStyle === 'object'
              ? { ...defaultSubmitButtonStyle, ...themeData.submitButtonStyle }
              : defaultSubmitButtonStyle,
            makeLegacyKey('submitButtonStyle')
          );

          saveToStorage(makeKey('fieldVariant'), themeData.fieldVariant || 'outlined', makeLegacyKey('fieldVariant'));

          // Store Firestore's lastUpdated (mode-aware + legacy mirror)
          saveToStorage(localLastUpdatedKey, finalFirestoreTimestamp, legacyLastUpdatedKey);


        } else {
          console.error('[FormThemeContext] Decompressed theme data is invalid. Using default theme settings.');
        }
      } catch (error) {
        console.error(`[FormThemeContext] Error fetching or decompressing theme data for formId: ${currentFormId}:`, error);
      } finally {
        setThemeLoading(false);
        setLoading(false, 'themeSettings'); // Notify LoadingContext
      }
    };

    loadThemeFromFirestore();
  }, [
    currentFormId,
    setLoading,
    formIdReady,
    isPublicPreviewMode,
    isBuilderRoute,
    effectiveViewMode,
    defaultColors,
    defaultFonts,
    defaultSubmitButtonStyle,
    makeKey,
    makeLegacyKey,
    makeLastUpdatedKey,
    makeLegacyLastUpdatedKey,
    getFromStorage,
    saveToStorage
  ]);


  // ---------------------------------------------------------------------------
  // RESET SETTINGS WHEN FORM ID CHANGES
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (
      previousFormId.current &&
      previousFormId.current !== currentFormId &&
      formIdReady
    ) {
      setSelectedTheme('Paper');
      setColors(defaultColors);
      setFonts(defaultFonts);
      setSubmitButtonStyle(defaultSubmitButtonStyle);
      setFieldVariant('outlined');
      isInitialThemeLoaded.current = false;
      previousFormId.current = currentFormId;
    }
  }, [currentFormId, defaultColors, defaultFonts, defaultSubmitButtonStyle, formIdReady]);

  // ---------------------------------------------------------------------------
  // INITIAL LOAD OF `selectedTheme` FROM STORAGE
  // ---------------------------------------------------------------------------
  useEffect(() => {
  if (formIdReady && (isBuilderRoute || isPublicPreviewMode)) {
      const storedTheme = getFromStorage(makeKey('selectedTheme'), makeLegacyKey('selectedTheme'));
      if (storedTheme) {
        // console.log(`[FormThemeContext] Loaded selectedTheme from storage:`, storedTheme);
        setSelectedTheme(storedTheme);
        isInitialThemeLoaded.current = true;
      } else {
        // console.log(`[FormThemeContext] No selectedTheme found in storage, using default.`);
      }
    }
}, [formIdReady, currentFormId, makeKey, makeLegacyKey, getFromStorage, isBuilderRoute, isPublicPreviewMode]);

  // Save `selectedTheme` to sessionStorage and localStorage
  useEffect(() => {
  if ((!isBuilderRoute && !isPublicPreviewMode) || !formIdReady) return;

    if (!isInitialThemeLoaded.current && !isPublicPreviewMode) return;

     console.log(`[FormThemeContext] Saving selectedTheme for formId: ${currentFormId}`);
    const timeoutId = setTimeout(() => {
    saveToStorage(makeKey('selectedTheme'), selectedTheme, makeLegacyKey('selectedTheme'));

    // TIMESTAMP LOGIC: Update lastUpdated as well (mode-aware + legacy mirror)
    saveToStorage(makeLastUpdatedKey(), new Date().toISOString(), makeLegacyLastUpdatedKey());

    }, 300);
    return () => clearTimeout(timeoutId); // Cleanup on component unmount or state change
}, [
  selectedTheme,
  currentFormId,
  formIdReady,
  isBuilderRoute,
  isPublicPreviewMode,
  makeKey,
  makeLegacyKey,
  makeLastUpdatedKey,
  makeLegacyLastUpdatedKey,
  saveToStorage
]);


  // ---------------------------------------------------------------------------
  // INITIAL LOAD OF `colors` FROM STORAGE
  // ---------------------------------------------------------------------------
  useEffect(() => {
  if (formIdReady && (isBuilderRoute || isPublicPreviewMode)) {
      const storedColors = getFromStorage(makeKey('colors'), makeLegacyKey('colors'));
      if (storedColors) {
         console.log(`[FormThemeContext] Loaded colors from storage:`, storedColors);
        setColors(storedColors);
      isInitialThemeLoaded.current = true;
      } else {
         console.log(`[FormThemeContext] No colors found in storage, using defaults.`);
      }
    }
  }, [formIdReady, currentFormId, makeKey, makeLegacyKey, getFromStorage, isBuilderRoute, isPublicPreviewMode]);


  // Save `colors` to sessionStorage and localStorage
  useEffect(() => {
    if ((!isBuilderRoute && !isPublicPreviewMode) || !formIdReady) return;
    if (!isInitialThemeLoaded.current && !isPublicPreviewMode) return;

     console.log(`[FormThemeContext] Saving colors for formId: ${currentFormId}`);
    const timeoutId = setTimeout(() => {
      saveToStorage(makeKey('colors'), colors, makeLegacyKey('colors'));

      // TIMESTAMP LOGIC: Update lastUpdated (mode-aware + legacy mirror)
      saveToStorage(makeLastUpdatedKey(), new Date().toISOString(), makeLegacyLastUpdatedKey());
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [colors, currentFormId, formIdReady, isBuilderRoute, isPublicPreviewMode, makeKey, makeLegacyKey, makeLastUpdatedKey, makeLegacyLastUpdatedKey, saveToStorage]);

  // ---------------------------------------------------------------------------
  // INITIAL LOAD OF `fonts` FROM STORAGE
  // ---------------------------------------------------------------------------
  useEffect(() => {
  if (formIdReady && (isBuilderRoute || isPublicPreviewMode)) {
      const storedFonts = getFromStorage(makeKey('fonts'), makeLegacyKey('fonts'));
      if (storedFonts) {
        setFonts(storedFonts);
      isInitialThemeLoaded.current = true;
      }
    }
  }, [formIdReady, currentFormId, makeKey, makeLegacyKey, getFromStorage, isBuilderRoute, isPublicPreviewMode]);


  // Save `fonts` to sessionStorage and localStorage
  useEffect(() => {
    if ((!isBuilderRoute && !isPublicPreviewMode) || !formIdReady) return;
    if (!isInitialThemeLoaded.current && !isPublicPreviewMode) return;

    // console.log(`[FormThemeContext] Saving fonts for formId: ${currentFormId}`);
    const timeoutId = setTimeout(() => {
      saveToStorage(makeKey('fonts'), fonts, makeLegacyKey('fonts'));
      // TIMESTAMP LOGIC: Update lastUpdated (mode-aware + legacy mirror)
      saveToStorage(makeLastUpdatedKey(), new Date().toISOString(), makeLegacyLastUpdatedKey());
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [fonts, currentFormId, formIdReady, isBuilderRoute, isPublicPreviewMode, makeKey, makeLegacyKey, makeLastUpdatedKey, makeLegacyLastUpdatedKey, saveToStorage]);

  // ---------------------------------------------------------------------------
  // INITIAL LOAD OF `submitButtonStyle` FROM STORAGE
  // ---------------------------------------------------------------------------
  useEffect(() => {
  if (formIdReady && (isBuilderRoute || isPublicPreviewMode)) {
      const storedButtonStyle = getFromStorage(
        makeKey('submitButtonStyle'),
        makeLegacyKey('submitButtonStyle')
      );
      if (storedButtonStyle) {
        setSubmitButtonStyle(storedButtonStyle);
        isInitialThemeLoaded.current = true;
      }
    }
  }, [formIdReady, currentFormId, makeKey, makeLegacyKey, getFromStorage, isBuilderRoute, isPublicPreviewMode]);


  // Save `submitButtonStyle` to sessionStorage and localStorage
  useEffect(() => {
    if ((!isBuilderRoute && !isPublicPreviewMode) || !formIdReady) return;
    if (!isInitialThemeLoaded.current && !isPublicPreviewMode) return;

    // console.log(`[FormThemeContext] Saving submitButtonStyle for formId: ${currentFormId}`);
    const timeoutId = setTimeout(() => {
      saveToStorage(makeKey('submitButtonStyle'), submitButtonStyle, makeLegacyKey('submitButtonStyle'));
      // TIMESTAMP LOGIC: Update lastUpdated (mode-aware + legacy mirror)
      saveToStorage(makeLastUpdatedKey(), new Date().toISOString(), makeLegacyLastUpdatedKey());
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [submitButtonStyle, formIdReady, currentFormId, isBuilderRoute, isPublicPreviewMode, makeKey, makeLegacyKey, makeLastUpdatedKey, makeLegacyLastUpdatedKey, saveToStorage]);

  // ---------------------------------------------------------------------------
  // INITIAL LOAD OF `fieldVariant` FROM STORAGE
  // ---------------------------------------------------------------------------
  useEffect(() => {
  if (formIdReady && (isBuilderRoute || isPublicPreviewMode)) {
      const storedFieldVariant = getFromStorage(makeKey('fieldVariant'), makeLegacyKey('fieldVariant'));
      if (storedFieldVariant) {
        setFieldVariant(storedFieldVariant);
        isInitialThemeLoaded.current = true;
      }
    }
  }, [formIdReady, currentFormId, makeKey, makeLegacyKey, getFromStorage, isBuilderRoute, isPublicPreviewMode]);


  // Save `fieldVariant` to sessionStorage and localStorage
  useEffect(() => {
      if ((!isBuilderRoute && !isPublicPreviewMode) || !formIdReady) return;
      if (!isInitialThemeLoaded.current && !isPublicPreviewMode) return;

    // console.log(`[FormThemeContext] Saving fieldVariant for formId: ${currentFormId}`);
    const timeoutId = setTimeout(() => {
      saveToStorage(makeKey('fieldVariant'), fieldVariant, makeLegacyKey('fieldVariant'));
      // TIMESTAMP LOGIC: Update lastUpdated (mode-aware + legacy mirror)
      saveToStorage(makeLastUpdatedKey(), new Date().toISOString(), makeLegacyLastUpdatedKey());
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [fieldVariant, formIdReady, currentFormId, isBuilderRoute, isPublicPreviewMode, makeKey, makeLegacyKey, makeLastUpdatedKey, makeLegacyLastUpdatedKey, saveToStorage]);

  // ---------------------------------------------------------------------------
  // HANDLERS TO UPDATE SETTINGS
  // ---------------------------------------------------------------------------
  const handleChangeColor = useCallback((type) => (color) => {
    if (!formIdReady && !isPublicPreviewMode) return;
   touchTheme();   // allow first-time edits for new forms

    setColors((prevColors) => {
      const updatedColors = { ...prevColors, [type]: color };
      saveToStorage(makeKey('colors'), updatedColors, makeLegacyKey('colors'));

      // TIMESTAMP LOGIC: Update lastUpdated (mode-aware + legacy mirror)
      saveToStorage(makeLastUpdatedKey(), new Date().toISOString(), makeLegacyLastUpdatedKey());

      return updatedColors;
    });
  }, [
    formIdReady,
    isPublicPreviewMode,
    makeKey,
    makeLegacyKey,
    saveToStorage,
    makeLastUpdatedKey,
    makeLegacyLastUpdatedKey
  ]);

  const handleFontChange = useCallback((type, font) => {
    if (!formIdReady && !isPublicPreviewMode) return;
    touchTheme();   // allow first-time edits for new forms

    setFonts((prevFonts) => {
      const updatedFonts = { ...prevFonts, [type]: font };
      saveToStorage(makeKey('fonts'), updatedFonts, makeLegacyKey('fonts'));

      // TIMESTAMP LOGIC: Update lastUpdated (mode-aware + legacy mirror)
      saveToStorage(makeLastUpdatedKey(), new Date().toISOString(), makeLegacyLastUpdatedKey());

      return updatedFonts;
    });
  }, [
    formIdReady,
    isPublicPreviewMode,
    makeKey,
    makeLegacyKey,
    saveToStorage,
    makeLastUpdatedKey,
    makeLegacyLastUpdatedKey
  ]);

  const handleVariantChange = useCallback((variant) => {
    if (!formIdReady && !isPublicPreviewMode) return;
   touchTheme();   // allow first-time edits for new forms

    setFieldVariant(variant);
    saveToStorage(makeKey('fieldVariant'), variant, makeLegacyKey('fieldVariant'));

    // TIMESTAMP LOGIC: Update lastUpdated (mode-aware + legacy mirror)
    saveToStorage(makeLastUpdatedKey(), new Date().toISOString(), makeLegacyLastUpdatedKey());
  }, [
    formIdReady,
    isPublicPreviewMode,
    makeKey,
    makeLegacyKey,
    saveToStorage,
    makeLastUpdatedKey,
    makeLegacyLastUpdatedKey
  ]);

  const handleStyleChange = useCallback((styleType, value) => {
    if (!formIdReady && !isPublicPreviewMode) return;
   touchTheme();   // allow first-time edits for new forms

    const stylesRequiringPx = ['width', 'height', 'borderRadius', 'fontSize'];
    let formattedValue = value;

    if (stylesRequiringPx.includes(styleType) && !isNaN(parseFloat(value))) {
      formattedValue = `${parseFloat(value)}px`;
    }

    setSubmitButtonStyle((prevStyle) => {
      const updatedStyle = { ...prevStyle, [styleType]: formattedValue || value };
      saveToStorage(makeKey('submitButtonStyle'), updatedStyle, makeLegacyKey('submitButtonStyle'));

      // TIMESTAMP LOGIC: Update lastUpdated (mode-aware + legacy mirror)
      saveToStorage(makeLastUpdatedKey(), new Date().toISOString(), makeLegacyLastUpdatedKey());

      return updatedStyle;
    });
  }, [
    formIdReady,
    isPublicPreviewMode,
    makeKey,
    makeLegacyKey,
    saveToStorage,
    makeLastUpdatedKey,
    makeLegacyLastUpdatedKey
  ]);



  // ---------------------------------------------------------------------------
  // FINAL CONTEXT VALUE
  // ---------------------------------------------------------------------------
  const contextValue = useMemo(() => ({
    formId: currentFormId,
    selectedTheme,
    setSelectedTheme: setSelectedThemeSafe,
    muiTheme,
    colors, handleChangeColor,
    fonts, handleFontChange,
    fieldVariant, handleVariantChange,
    submitButtonStyle, handleStyleChange,
    defaultSubmitButtonStyle,
    themeLoading,
  }), [
    currentFormId,
    selectedTheme,
    muiTheme,
    colors,
    fonts, 
    fieldVariant,
    submitButtonStyle,
    handleChangeColor, 
    handleFontChange,
    handleVariantChange,
    handleStyleChange, 
    themeLoading,
    setSelectedThemeSafe
  ]);

  return (
    <FormThemeContext.Provider value={contextValue}>
      {children}
    </FormThemeContext.Provider>
  );
};

export default FormThemeProvider;
